/**
 * BatchActionBar — floating bottom dock for batch actions on selected items.
 *
 * Elevated, centered dock with subtle border, soft blur,
 * high-contrast selection pill, and keyboard-friendly actions.
 */

import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface BatchActionBarProps {
  /** Number of selected items */
  count: number;
  /** Label for the items (e.g. "walkthrough", "finding", "source") */
  noun?: string;
  /** Clear selection callback */
  onClear: () => void;
  /** Placement variant: floating bottom dock (default) or contextual inline toolbar */
  variant?: "floating" | "inline";
  /** Action buttons */
  children: ReactNode;
}

export function BatchActionBar({
  count,
  noun = "item",
  onClear,
  variant = "floating",
  children,
}: BatchActionBarProps) {
  if (count === 0) return null;

  const plural = count === 1 ? noun : `${noun}s`;

  return (
    <div
      className={cn(
        variant === "inline"
          ? "flex items-center gap-2 rounded-sm border border-border bg-card px-2.5 py-1 text-card-foreground shadow-xs transition-all duration-150 animate-in fade-in-0"
          : "fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex max-w-[calc(100vw-3rem)] items-center gap-2 rounded-lg border border-border/80 bg-popover/95 px-3.5 py-1.5 text-popover-foreground shadow-2xl backdrop-blur-md transition-all duration-150 animate-in fade-in-0 slide-in-from-bottom-2",
      )}
      role="toolbar"
      aria-label={`Batch actions for ${count} selected ${plural}`}
      aria-live="polite"
    >
      <span className="flex items-center gap-1 rounded-sm bg-muted px-2.5 py-0.5 text-xs font-semibold tabular-nums text-foreground">
        {count} selected
      </span>
      <div className="h-4 w-px bg-border/80" aria-hidden="true" />
      <div className="flex items-center gap-1 overflow-x-auto py-0.5 scrollbar-none">
        {children}
      </div>
      <div className="h-4 w-px bg-border/80" aria-hidden="true" />
      <Button
        variant="ghost"
        size="sm"
        className="h-7 rounded-sm px-2 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        onClick={onClear}
        aria-label="Clear selection"
      >
        <X className="mr-1 h-3.5 w-3.5" />
        Clear
      </Button>
    </div>
  );
}

/**
 * Styled button for use inside BatchActionBar.
 * Matches floating dock styling with clear hover states.
 */
export function BatchActionButton({
  className,
  ...props
}: React.ComponentProps<typeof Button>) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn(
        "h-7 rounded-sm px-3 text-xs font-medium text-foreground hover:bg-muted transition-colors whitespace-nowrap",
        className,
      )}
      {...props}
    />
  );
}
